const { OB_TRUST_INFO, OB_VISITOR_ID, OB_VISITOR_REQ_ID } = require('../../constants/cookieConstants');
const { getHmacDigestValue } = require('../../utils/serverSignature');
const { getIpFromHeaders } = require('../utils');
const serverAPI = require('../utils/serverAPI');

const isFingerPrintEnabled = () => process.env.FINGER_PRINT_ENABLED === 'true';

const getExpirationTime = () => {
  const ONE_MINUTE = 60 * 1000;
  const minutesToExpire = process.env.APP_ENV === 'production' ? 30 : 5;

  return new Date(Date.now() + minutesToExpire * ONE_MINUTE);
};

const processTrustInfo = trustInfo => ({
  ...trustInfo,
  ...(process.env.SUSPICIOUS_ACTIVITY_FORCED_STATUS && { status: process.env.SUSPICIOUS_ACTIVITY_FORCED_STATUS }),
  ...(process.env.SUSPICIOUS_ACTIVITY_FORCED_VISITOR_TYPE && { visitorType: process.env.SUSPICIOUS_ACTIVITY_FORCED_VISITOR_TYPE }),
})

const getTrustInformation = async ({ ip, userAgent, visitorId, requestId }) => {
  try {
    const trustInfo = await serverAPI.get('fingerprints/trust', {
      ip_address: ip,
      user_agent: userAgent,
      visitor_id: visitorId,
      request_id: requestId,
      source: 'server',
    });

    return trustInfo?.data?.[0];
  } catch (ex) {
    return null;
  }
};

const convertTrustInfoToCookieValue = (trustInfo, timestamp, signature) => {
  if (!trustInfo) {
    throw Error('Missing Trust Information');
  }

  const { visitorType, status, trust, bot, visitorId, requestId, source, ipAddress, createdAt } = trustInfo;
  const { level, score } = trust || {};
  const { botType, result } = bot || {};

  const orderInfo = [
    timestamp,
    signature,
    visitorType,
    status,
    level,
    score,
    botType,
    result,
    visitorId,
    requestId,
    source,
    ipAddress,
    createdAt,
  ];

  return orderInfo.join('|');
};

const convertCookieValueToTrustInfo = value => {
  if (!value) {
    return null;
  }

  const [
    timestamp,
    signature,
    visitorType,
    status,
    level,
    score,
    botType,
    result,
    visitorId,
    requestId,
    source,
    ipAddress,
    createdAt,
  ] = value.split('|');

  return {
    signature,
    timestamp,
    visitorType,
    status,
    trust: {
      level,
      score,
    },
    bot: {
      botType,
      result,
    },
    visitorId,
    requestId,
    source,
    ipAddress,
    createdAt,
  };
};

const createSignature = ({ ip, userAgent, visitorType, status, expirationTime, timestamp }) => {
  const timestampString = timestamp || Math.round(expirationTime.getTime() / 1000);
  const stringToHash = `${ip}|${visitorType}|${status}|${timestampString}`;
  const signature = getHmacDigestValue(stringToHash, userAgent);

  return signature;
};

const validateSignature = ({ signatureToVerify, ip, userAgent, visitorType, status, timestamp }) => {
  const currentTimestamp = Math.round(new Date().getTime() / 1000);

  if (timestamp < currentTimestamp) {
    return false;
  }

  return signatureToVerify === createSignature({ ip, userAgent, visitorType, status, timestamp });
};

const setTrustInfoCookie = (req, res, { trustInfo }) => {
  const ip = getIpFromHeaders(req) || null;
  const userAgent = req.headers['user-agent'];
  const expirationTime = getExpirationTime();
  const { visitorType, status } = trustInfo || {};
  const timestamp = Math.round(expirationTime.getTime() / 1000);
  const signature = createSignature({ ip, userAgent, visitorType, status, timestamp });

  res.cookie(OB_TRUST_INFO, convertTrustInfoToCookieValue(trustInfo, timestamp, signature), {
    sameSite: 'lax',
  });
};

const trustInfoMiddleware = async (req, res, next) => {
  if (!isFingerPrintEnabled() || (req.path.indexOf('.') >= 0 && !req.path.includes('/_next/data/'))) {
    return next();
  }

  const ip = getIpFromHeaders(req) || null;
  const visitorId = req.cookies[OB_VISITOR_ID];
  const requestId = req.cookies[OB_VISITOR_REQ_ID];

  const userAgent = req.headers['user-agent'];

  let trustInfo = null;

  if (req.cookies[OB_TRUST_INFO]) {
    try {
      const parsedInfo = convertCookieValueToTrustInfo(req.cookies[OB_TRUST_INFO]);
      const { signature: cookieSignature, timestamp, ...trustInfoParsed } = parsedInfo || {};
      const { visitorType, status } = trustInfoParsed || {};
      const isValidSignature = validateSignature({
        signatureToVerify: cookieSignature,
        ip,
        userAgent,
        visitorType,
        status,
        timestamp,
      });

      if (isValidSignature) {
        trustInfo = trustInfoParsed;
      }
    } catch {
      trustInfo = null;
    }
  }

  if (!trustInfo) {
    trustInfo = await getTrustInformation({ ip, userAgent, visitorId, requestId });

    if (trustInfo) {
      setTrustInfoCookie(req, res, { trustInfo });
    }
  }

  req.trustInfo = processTrustInfo(trustInfo);

  return next();
};

module.exports = { trustInfoMiddleware, convertCookieValueToTrustInfo, setTrustInfoCookie, processTrustInfo };
